import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Dialog } from 'primereact/dialog';

import FormTributos from '../components/FormTributos';
import { validarRegrasTributarias, resumoErros } from '../regrasTributarias';
import {
  obterProduto, incluirProduto, atualizarProduto, conferirProduto, desmarcarConferido, excluirProduto, mensagemErro,
} from '../tributosApi';
import { SituacaoProdutoTag, normalizarTributos } from '../util';

export default function ProdutoForm({ onAlteracao }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const novo = !id;

  const [form, setForm] = useState(normalizarTributos(null));
  const [carregando, setCarregando] = useState(!novo);
  const [salvando, setSalvando] = useState(false);
  const [confirmarExclusao, setConfirmarExclusao] = useState(false);

  useEffect(() => {
    if (novo) {
      setForm(normalizarTributos(null));
      setCarregando(false);
      return;
    }
    setCarregando(true);
    obterProduto(id)
      .then(p => setForm(normalizarTributos(p)))
      .catch(e => {
        toast.error(mensagemErro(e, 'Produto não encontrado.'));
        navigate('/tributos/produtos');
      })
      .finally(() => setCarregando(false));
  }, [id, novo, navigate]);

  const executar = async (acao, mensagem) => {
    if (salvando) return;
    setSalvando(true);
    try {
      const salvo = await acao();
      toast.success(mensagem);
      onAlteracao();
      if (novo) {
        // Inclusão: limpa a tela de cadastro para o próximo produto
        setForm(normalizarTributos(null));
        const conteudo = document.querySelector('.tb-content');
        if (conteudo) conteudo.scrollTo(0, 0);
        const campoEan = document.querySelector('.tb-form input');
        if (campoEan) campoEan.focus();
      } else if (salvo && salvo.id) {
        setForm(normalizarTributos(salvo));
      }
    } catch (e) {
      toast.error(mensagemErro(e));
    } finally {
      setSalvando(false);
    }
  };

  const salvar = () => executar(
    () => (novo ? incluirProduto(form) : atualizarProduto(id, form)),
    'Produto salvo.',
  );

  const salvarConferir = () => {
    const { erros } = validarRegrasTributarias(form);
    if (erros.length) {
      toast.error(`Corrija a tributação antes de conferir: ${resumoErros(erros)}`);
      return Promise.resolve();
    }
    return executarConferir();
  };

  const executarConferir = () => executar(
    () => (novo ? incluirProduto(form, true) : conferirProduto(id, form)),
    'Produto salvo e marcado como conferido.',
  );

  const desmarcar = () => executar(() => desmarcarConferido(id), 'Marcação de conferido removida.');

  const excluir = async () => {
    setSalvando(true);
    try {
      await excluirProduto(id);
      toast.success('Produto excluído.');
      onAlteracao();
      navigate('/tributos/produtos');
    } catch (e) {
      toast.error(mensagemErro(e));
    } finally {
      setSalvando(false);
      setConfirmarExclusao(false);
    }
  };

  if (carregando) {
    return <div className="tb-page"><div className="tb-empty"><i className="pi pi-spin pi-spinner" /> Carregando…</div></div>;
  }

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <button type="button" className="tb-btn tb-btn-link tb-back" onClick={() => navigate('/tributos/produtos')}>
            <i className="pi pi-arrow-left" /> Base de tributos
          </button>
          <h1>{novo ? 'Novo produto' : form.descricao || 'Produto'}</h1>
          {!novo ? (
            <p className="tb-head-meta">
              <SituacaoProdutoTag produto={form} />
              {form.dataCadastro ? <span>Cadastrado em {form.dataCadastro}</span> : null}
              {form.dataConferencia ? <span>Conferido em {form.dataConferencia}</span> : null}
            </p>
          ) : null}
        </div>
      </div>

      <div className="tb-card tb-form-card">
        <FormTributos valor={form} onChange={setForm} bloquearEan={false} />

        <div className="tb-actions">
          <div className="tb-actions-left">
            {!novo ? (
              <button type="button" className="tb-btn tb-btn-danger-ghost" onClick={() => setConfirmarExclusao(true)} disabled={salvando}>
                <i className="pi pi-trash" /> Excluir
              </button>
            ) : null}
            {!novo && form.conferido ? (
              <button type="button" className="tb-btn tb-btn-ghost" onClick={desmarcar} disabled={salvando}>
                <i className="pi pi-times-circle" /> Desmarcar conferido
              </button>
            ) : null}
          </div>
          <div className="tb-actions-right">
            <button type="button" className="tb-btn tb-btn-ghost" onClick={salvar} disabled={salvando}>
              <i className="pi pi-save" /> Salvar
            </button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={salvarConferir} disabled={salvando}>
              {salvando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check-circle" />} Salvar e marcar conferido
            </button>
          </div>
        </div>
      </div>

      <Dialog
        className="tb-dialog"
        header="Excluir produto"
        visible={confirmarExclusao}
        onHide={() => setConfirmarExclusao(false)}
        style={{ width: '26rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setConfirmarExclusao(false)}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-danger" onClick={excluir} disabled={salvando}>Excluir</button>
          </div>
        )}
      >
        <p>Confirma a exclusão do produto <strong>{form.codigoEan || form.descricao}</strong> da base de tributos?</p>
      </Dialog>
    </div>
  );
}

ProdutoForm.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
